// 🆕 [2A.4] Page admin minimaliste — affiche les events captés par
// /api/diagnostics/client-events. Réservée au propriétaire.
// 🛡️ [Phase 57.6 — 2026-06-14] Ajout d'une carte "Outils de maintenance"
// avec bouton "Forcer purge clients" (cache PWA permanent fix).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, AlertTriangle, AlertCircle, Info, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { purgeCachesAndReload } from '@/hooks/useAppVersionWatcher';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL =
  (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const EVENT_TYPES = [
  '',
  'permissions_fetch_failed',
  'user_became_null',
  'api_error',
  'sidebar_empty_rendered',
  'auth_loading_stuck',
];
const SEVERITIES = ['', 'warning', 'error', 'critical'];

function sevBadge(sev) {
  if (sev === 'critical') return <Badge variant="destructive" className="bg-red-600">critical</Badge>;
  if (sev === 'error') return <Badge variant="destructive">error</Badge>;
  return <Badge variant="secondary">warning</Badge>;
}

function evIcon(sev) {
  if (sev === 'critical') return <AlertCircle className="w-4 h-4 text-red-600" />;
  if (sev === 'error') return <AlertTriangle className="w-4 h-4 text-orange-600" />;
  return <Info className="w-4 h-4 text-slate-500" />;
}

export default function AdminDiagnostics() {
  const { user, token } = useAuth();
  const [email, setEmail] = useState('');
  const [eventType, setEventType] = useState('');
  const [severity, setSeverity] = useState('');
  const [sinceHours, setSinceHours] = useState(24);
  const [events, setEvents] = useState([]);
  const [aggregates, setAggregates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  const fetchEvents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (email.trim()) params.set('email', email.trim());
      if (eventType) params.set('event_type', eventType);
      if (severity) params.set('severity', severity);
      params.set('since_hours', String(sinceHours));
      params.set('limit', '500');
      const res = await fetch(`${API_URL}/api/diagnostics/client-events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setEvents([]);
        setAggregates([]);
      } else {
        const data = await res.json();
        setEvents(data.events || []);
        setAggregates(data.aggregates || []);
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [token, email, eventType, severity, sinceHours]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (!estProprietaire(user)) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center text-slate-600">
            Accès réservé au propriétaire.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-2 sm:p-4 max-w-7xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Diagnostics clients</h1>
          <p className="text-sm text-slate-500">
            Télescope des erreurs côté navigateur (menu vide, permissions, API, etc.). TTL : 90 jours.
          </p>
        </div>
        <Button onClick={fetchEvents} disabled={loading} variant="outline" size="sm" data-testid="diag-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
        </Button>
      </div>

      {/* 🛡️ [Phase 57.6] Outils de maintenance — cache PWA */}
      <MaintenanceCard token={token} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filtres</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Email employé</label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ex: greg0109p@hotmail.com"
              data-testid="diag-filter-email"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Type d'event</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="diag-filter-eventtype"
            >
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t || '(tous)'}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Gravité</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="diag-filter-severity"
            >
              {SEVERITIES.map(s => <option key={s} value={s}>{s || '(toutes)'}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Depuis (heures)</label>
            <Input
              type="number"
              min={1}
              max={24 * 30}
              value={sinceHours}
              onChange={(e) => setSinceHours(parseInt(e.target.value, 10) || 24)}
              data-testid="diag-filter-since"
            />
          </div>
        </CardContent>
      </Card>

      {aggregates.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top occurrences (user × event_type)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500 border-b">
                  <tr>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Event type</th>
                    <th className="py-2">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregates.slice(0, 20).map((a, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-4 text-slate-800">{a.user_email || '(sans token)'}</td>
                      <td className="py-1 pr-4 text-slate-600">{a.event_type}</td>
                      <td className="py-1 font-mono">{a.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Événements {loading ? <span className="text-slate-400 text-xs">(chargement…)</span> : `(${events.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="p-3 mb-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="diag-error">
              Erreur : {error}
            </div>
          )}
          {events.length === 0 && !loading ? (
            <p className="text-sm text-slate-500 text-center py-6">Aucun événement pour ces filtres.</p>
          ) : (
            <div className="space-y-2">
              {events.map((ev, i) => {
                const key = ev.id || i;
                const isExp = !!expanded[key];
                return (
                  <div key={key} className="border border-slate-200 rounded-md p-2 hover:bg-slate-50 text-sm">
                    <div className="flex items-start gap-2 flex-wrap">
                      <span className="flex-shrink-0 mt-0.5">{evIcon(ev.severity)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{ev.event_type}</span>
                          {sevBadge(ev.severity)}
                          <span className="text-xs text-slate-400">{ev.server_timestamp}</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">
                          <span className="font-medium">{ev.user_email || '(non auth)'}</span>
                          {ev.user_role && <> · <span>{ev.user_role}</span></>}
                          {ev.url && <> · <span className="text-slate-400 break-all">{ev.url}</span></>}
                        </div>
                        {ev.user_agent && (
                          <div className="text-[10px] text-slate-400 mt-0.5 break-all">{ev.user_agent}</div>
                        )}
                      </div>
                      <button
                        onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {isExp ? 'Masquer' : 'Détails'}
                      </button>
                    </div>
                    {isExp && (
                      <pre className="mt-2 p-2 bg-slate-900 text-slate-100 text-[11px] rounded overflow-x-auto">
                        {JSON.stringify(ev, null, 2)}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// 🛡️ [Phase 57.6 — 2026-06-14] MaintenanceCard
// Carte "Outils de maintenance" affichée en haut de la page AdminDiagnostics.
// Permet au propriétaire de :
//   1. Voir la version baked vs. version serveur courante
//   2. Forcer un rafraîchissement local (purge cache + reload)
//   3. Forcer un Clear-Site-Data sur le navigateur en cours (et tous ceux qui
//      hit l'endpoint ensuite) — TOUS LES NAVIGATEURS qui font un GET sur la
//      réponse purgent immédiatement leur cache + storage. Action nucléaire.
// ─────────────────────────────────────────────────────────────────────────────
function MaintenanceCard({ token }) {
  const [serverVersion, setServerVersion] = useState(null);
  const [serverDeployedAt, setServerDeployedAt] = useState(null);
  const [purging, setPurging] = useState(false);
  const bakedVersion = (process.env.REACT_APP_VERSION || '').trim() || '(non défini)';

  const fetchVersion = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/version?_=${Date.now()}`, {
        timeout: 8000,
      });
      setServerVersion(res?.data?.version_id || '(inconnu)');
      setServerDeployedAt(res?.data?.deployed_at || null);
    } catch {
      setServerVersion('(erreur réseau)');
    }
  }, []);

  useEffect(() => {
    fetchVersion();
  }, [fetchVersion]);

  const isOutdated = serverVersion && bakedVersion && serverVersion !== bakedVersion;

  const handleLocalPurge = async () => {
    if (!window.confirm(
      'Vider le cache + service worker de CE navigateur et recharger ?\n\n' +
      'Tu seras potentiellement déconnecté(e) et devras te reconnecter.'
    )) return;
    await purgeCachesAndReload();
  };

  const handleForceServerPurge = async () => {
    if (!window.confirm(
      '⚠️ ACTION NUCLÉAIRE\n\n' +
      'Ceci va envoyer le header Clear-Site-Data à TON navigateur uniquement.\n' +
      'Tu seras déconnecté(e) et perdras tous les caches PWA.\n\n' +
      'Si tu veux purger TOUS les navigateurs des employés, demande-leur de\n' +
      'cliquer sur le toast "Recharger" qui apparaîtra automatiquement lors\n' +
      'd\'un changement de version.\n\nContinuer ?'
    )) return;
    setPurging(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/force-purge-clients`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('✅ Purge envoyée — votre navigateur va se recharger');
      // Le serveur a renvoyé Clear-Site-Data, le navigateur va purger.
      // On déclenche aussi un reload manuel pour s'assurer du nettoyage.
      setTimeout(() => { window.location.reload(); }, 500);
    } catch (err) {
      toast.error(`Erreur : ${err?.response?.data?.detail || err.message}`);
      setPurging(false);
    }
  };

  return (
    <Card data-testid="maintenance-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-600" />
          Outils de maintenance — Cache PWA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* État des versions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-md p-3 border" data-testid="version-baked">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Version baked (ce navigateur)</p>
            <p className="font-mono text-sm text-slate-800 mt-1">{bakedVersion}</p>
          </div>
          <div
            className={`rounded-md p-3 border ${isOutdated ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-200'}`}
            data-testid="version-server"
          >
            <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1">
              Version serveur courante
              {isOutdated && (
                <Badge variant="destructive" className="ml-1 text-[10px]">obsolète</Badge>
              )}
            </p>
            <p className="font-mono text-sm text-slate-800 mt-1">{serverVersion || '…'}</p>
            {serverDeployedAt && (
              <p className="text-[11px] text-slate-500 mt-1">
                Déployé : {new Date(serverDeployedAt).toLocaleString('fr-CA')}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
          <Button
            size="sm"
            variant="outline"
            onClick={handleLocalPurge}
            data-testid="btn-local-purge"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Vider mon cache local + recharger
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleForceServerPurge}
            disabled={purging}
            data-testid="btn-force-purge"
          >
            <Trash2 className={`w-4 h-4 mr-2 ${purging ? 'animate-pulse' : ''}`} />
            {purging ? 'Purge en cours…' : 'Forcer purge (Clear-Site-Data)'}
          </Button>
        </div>

        <p className="text-[11px] text-slate-500 italic pt-1">
          ℹ️ Les employés sont notifiés automatiquement via un toast persistant
          quand une nouvelle version est déployée. Ils cliquent sur « Recharger »
          et leur cache se purge tout seul. Ces boutons sont réservés aux cas
          critiques où la notification automatique ne se déclenche pas.
        </p>
      </CardContent>
    </Card>
  );
}
